import { css } from '@emotion/react';
import { SchedulerV3 } from '@frontend/api-schedule-v3';
import { useTranslation } from '@frontend/i18n';
import { breakpoints } from '@frontend/responsiveness';
import { theme } from '@frontend/theme';
import { useAlert, Button, DropdownField, FormRow, NumberField, useForm, Text } from '@frontend/design-system';
import { bookingSiteSetupLeadTimeTrackingIds } from '../trackingIds';
import { convertMinutesToDaysAndHours, convertDaysAndHoursToMinutes, getMaximumLeadDurationError } from './helpers';

const { saveButton, durationInput, timeUnitDropdown } = bookingSiteSetupLeadTimeTrackingIds;

type BookingSiteAppointmentLeadTimeProps = {
  locationId: string;
  leadDuration: number;
};

export const AppointmentLeadTimeForm = ({ locationId, leadDuration, ...rest }: BookingSiteAppointmentLeadTimeProps) => {
  const { t } = useTranslation('scheduleGlobalSettings');
  const alert = useAlert();

  const { mutateAsync, isLoading: isUpdating } = SchedulerV3.Mutations.useUpdateLocationSettingsMutation();

  const { values, getFieldProps } = useForm({
    fields: {
      advanceBookingTime: {
        type: 'number',
        value: String(convertMinutesToDaysAndHours(leadDuration).duration),
        allowDecimal: false,
        allowNegative: false,
        validateOnChange: true,
      },
      timeUnit: {
        type: 'dropdown',
        value: convertMinutesToDaysAndHours(leadDuration).timeUnit,
      },
    },
  });

  const amountInMinutes = convertDaysAndHoursToMinutes(
    Number(values.advanceBookingTime),
    values.timeUnit as 'days' | 'hours'
  );

  const updateLeadTime = async () => {
    try {
      const locationSettingResponse = await mutateAsync({
        locationId: locationId,
        leadDuration: amountInMinutes,
      });
      alert.success(t('Advance booking duration updated successfully'));
      return Promise.resolve(locationSettingResponse);
    } catch (error) {
      console.error('Error updating lead duration:', error);
      alert.error(t('Failed to update advance booking duration'));
      return Promise.reject(error);
    }
  };

  const errorText = getMaximumLeadDurationError(Number(values.advanceBookingTime), values.timeUnit as 'days' | 'hours');

  const hasError = Boolean(errorText);

  const disabled = leadDuration === amountInMinutes || hasError;

  return (
    <FormRow css={formRowStyle} {...rest}>
      <div css={{ position: 'relative' }}>
        <NumberField
          actionTrackingId={durationInput}
          {...getFieldProps('advanceBookingTime')}
          label={t('Duration')}
          css={css({ width: 100 })}
        />
        {Number(values.advanceBookingTime) > 0 && hasError && (
          <Text
            color='error'
            css={{
              position: 'absolute',
              bottom: -21,
              left: 0,
              width: '300px',
              fontSize: theme.fontSize(12),
            }}
          >
            {errorText}
          </Text>
        )}
      </div>
      <DropdownField
        {...getFieldProps('timeUnit')}
        label={t('Time Unit')}
        css={css({ width: 160, textTransform: 'capitalize' })}
      >
        <DropdownField.Option key='days' value='days' trackingId={`${timeUnitDropdown}:days`}>
          {t('Days')}
        </DropdownField.Option>

        <DropdownField.Option key='hours' value='hours' trackingId={`${timeUnitDropdown}:hours`}>
          {t('Hours')}
        </DropdownField.Option>
      </DropdownField>
      <Button
        trackingId={saveButton}
        variant='primary'
        size='large'
        onClick={updateLeadTime}
        disabled={disabled}
        loading={isUpdating}
      >
        {t('Save')}
      </Button>
    </FormRow>
  );
};

const formRowStyle = css({
  marginTop: theme.spacing(0.5),
  marginBottom: theme.spacing(0.5),
  [`@media (max-width: ${breakpoints.small.max}px)`]: {
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: theme.spacing(1),
    '& > *': {
      marginBottom: theme.spacing(1),
    },
    '& > *:last-child': {
      marginBottom: 0,
    },
  },
});
